import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Environment, PerspectiveCamera, OrbitControls, Html } from '@react-three/drei';
import { EffectComposer, N8AO } from '@react-three/postprocessing';
import Room from './Room';
import { SceneCamera } from './Scene/components/SceneCamera';
import { SceneControls } from './Scene/components/SceneControls';
import AvatarTest from './Scene/components/AvatarTest';
import { Lighting } from './Scene/components/Lighting';
import { FOVSlider, ControlInstructions, ModeIndicator } from './Scene/components/UI';
import { SCENE_DIMENSIONS, CAMERA_CONFIG } from './Scene/config/sceneConfig';
import { getRoomConfig } from './Scene/config/roomTypes';
import MobileControls from './MobileControls';
import MobileJoystick from './Scene/components/MobileJoystick';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { useSceneManager } from './Scene/managers/SceneManager';
import { PointerLockProvider, usePointerLockContext } from './Scene/contexts/PointerLockContext';
import * as THREE from 'three';
import { galleries as galleriesApi } from '../../services/api';
import { useHedera } from '../../contexts/HederaContext';
import { resolvePath } from '../../utils/pathUtils';
import GalleryLoadingOverlay from '../UI/GalleryLoadingOverlay';
import FloatingTooltip from '../UI/FloatingTooltip';




const Scene = forwardRef(({ onSpotSelect, assets, isViewMode: propIsViewMode, galleryId, isRightMenuOpen, initialFov, onAssetsUpdate: propOnAssetsUpdate, currentGallery, glbPath: propGlbPath }, ref) => {
    const { isMobile } = useMobileDetection();
    console.log('Scene received galleryId:', galleryId);
    console.log('Scene initial assets:', assets);
    console.log('Scene received currentGallery:', currentGallery);

    const { accountId } = useHedera();
    
    // Use currentGallery prop instead of fetching data
    const galleryData = currentGallery;
    
    // Loading states for overlay
    const [isLoadingGallery, setIsLoadingGallery] = useState(false);
    const [roomLoaded, setRoomLoaded] = useState(false);
    const [texturesLoaded, setTexturesLoaded] = useState(false);
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    // Don't show floating tooltip if we're loading in view mode from URL
    // Also track if user has ever entered view mode to hide tooltip permanently
    const [showFloatingTooltip, setShowFloatingTooltip] = useState(!propIsViewMode);
    const [hasEnteredViewMode, setHasEnteredViewMode] = useState(false);
    
    // State for hover interaction in view mode
    const [hoveredAssetId, setHoveredAssetId] = useState(null);

    // Reset loading states when gallery changes
    useEffect(() => {
        if (currentGallery && galleryId) {
            console.log('Scene using currentGallery prop:', {
                id: currentGallery._id,
                name: currentGallery.name,
                roomType: currentGallery.roomType,
                glbPath: currentGallery.glbPath
            });
            
            // Reset all loading states
            setIsLoadingGallery(true);
            setRoomLoaded(false);
            setTexturesLoaded(false);
            setAvatarLoaded(false);
        }
    }, [currentGallery, galleryId]);

    // Callback to hide loading overlay
    const handleLoadingComplete = () => {
        console.log('Scene: Hiding loading overlay');
        setIsLoadingGallery(false);
    };

    // Debug loading states
    useEffect(() => {
        console.log('Scene loading states:', {
            isLoadingGallery,
            roomLoaded,
            texturesLoaded,
            avatarLoaded
        });
    }, [isLoadingGallery, roomLoaded, texturesLoaded, avatarLoaded]);

    const sceneManager = useSceneManager({ 
        onSpotSelect: (spotId, isEditMode) => {
            // Pass the effectiveViewMode state to parent
            onSpotSelect(spotId, isEditMode, sceneManager?.effectiveViewMode);
        }, 
        assets, 
        isViewMode: propIsViewMode, 
        initialFov 
    });

    const {
        controlsRef,
        sceneContainerRef,
        isLocked,
        handleLock,
        handleUnlock,
        managedAssets,
        selectedAsset,
        handleSpotSelection,
        canInteract,
        fov,
        setFov,
        effectiveViewMode,
        handleViewModeToggle,
        setSelectedSpot,
        setCanInteract,
        isShowMode
    } = sceneManager;

    const [currentAssets, setCurrentAssets] = useState(assets);
    const rendererRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);

    useEffect(() => {
        console.log('Scene assets updated:', assets);
        setCurrentAssets(assets);
    }, [assets]);

    const handleAssetsUpdate = useCallback((newAssets) => {
        console.log('Scene handleAssetsUpdate:', newAssets);
        setCurrentAssets(newAssets);
        if (propOnAssetsUpdate) {
            propOnAssetsUpdate(newAssets);
        }
    }, [propOnAssetsUpdate]);

    // Prevent any editing capabilities in view mode
    useEffect(() => {
        if (effectiveViewMode) {
            setSelectedSpot(null);
            setCanInteract(false);
        }
    }, [effectiveViewMode, setSelectedSpot, setCanInteract]);

    // Track when user enters view mode for the first time to hide tooltip
    useEffect(() => {
        if (effectiveViewMode && !hasEnteredViewMode) {
            setHasEnteredViewMode(true);
            setShowFloatingTooltip(false);
        }
    }, [effectiveViewMode, hasEnteredViewMode]);

    // E key handler for view mode interactions and Delete key for asset removal
    useEffect(() => {
        const handleKeyPress = (event) => {
            // E key for NFT details in view mode
            if (event.key.toLowerCase() === 'e' && effectiveViewMode && hoveredAssetId && currentAssets[hoveredAssetId]?.url) {
                // Show NFT details modal
                onSpotSelect(hoveredAssetId, false, effectiveViewMode);
            }
            
            // Delete key for removing assets in edit mode
            if ((event.key === 'Delete' || event.key === 'Backspace') && !effectiveViewMode && selectedAsset && currentAssets[selectedAsset]) {
                // Remove asset from selected spot by setting it to null
                const updatedAssets = { ...currentAssets };
                delete updatedAssets[selectedAsset];
                
                // Update assets state
                if (propOnAssetsUpdate) {
                    propOnAssetsUpdate(updatedAssets);
                }
                
                // Also trigger server update if we have gallery ID and account ID
                if (galleryId && accountId) {
                    galleriesApi.updateAssets(galleryId, updatedAssets, accountId).catch(error => {
                        console.error('Failed to update assets on server:', error);
                    });
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [effectiveViewMode, hoveredAssetId, currentAssets, onSpotSelect, selectedAsset, propOnAssetsUpdate, galleryId, accountId]);

    // Handle hover events from DisplaySpot
    const handleAssetHover = (assetId) => {
        setHoveredAssetId(assetId);
    };

    // Expose FOV state and renderer through ref
    useImperativeHandle(ref, () => ({
        fov,
        setFov,
        getRenderer: () => rendererRef.current,
        getScene: () => sceneRef.current,
        getCamera: () => cameraRef.current
    }), [fov, setFov]);

    const handleBackgroundClick = (event) => {
        // Only handle direct background clicks and prevent in view mode
        if (event.target === event.currentTarget && !effectiveViewMode) {
            handleSpotSelection(null);
        }
    };

    // Extract the roomType and glbPath from the gallery data or currentGallery prop, and log for debugging
    console.log('Gallery data roomType:', galleryData?.roomType);
    console.log('Gallery data glbPath:', galleryData?.glbPath);
    console.log('currentGallery roomType:', currentGallery?.roomType);
    console.log('currentGallery glbPath:', currentGallery?.glbPath);
    console.log('propGlbPath:', propGlbPath);
    
    // Prioritize roomType from gallery data, then from currentGallery prop, then default to 'classic'
    let roomType = galleryData?.roomType || currentGallery?.roomType || 'classic';
    // Get glbPath if available
    let glbPath = galleryData?.glbPath || currentGallery?.glbPath || propGlbPath || null;
    
    // Debug logs to track the issue
    console.log('=== ROOM TYPE DEBUG ===');
    console.log('galleryData:', galleryData);
    console.log('currentGallery:', currentGallery);
    console.log('galleryData?.roomType:', galleryData?.roomType);
    console.log('currentGallery?.roomType:', currentGallery?.roomType);
    console.log('Final roomType:', roomType);
    console.log('=====================');
    
    // Force hexagonal room to use the correct version with hallway
    if (roomType === 'hexagonal') {
        console.log('Using hexagonal room with hallway');
        // Make sure we're not using a GLB file for the hexagonal room
        if (glbPath && glbPath.includes('hex')) {
            console.log('Clearing GLB path to ensure built-in hexagonal room with hallway is used');
            glbPath = null;
        }
    }
    
    console.log('Using room type for rendering:', roomType);
    console.log('Using GLB path:', glbPath);
    
    // Get room configuration including spawn point
    const roomConfig = getRoomConfig(roomType, glbPath);
    const spawnPoint = roomConfig?.spawnPoint || null;
    console.log('Room spawn point:', spawnPoint);

    return (
        <PointerLockProvider>
            <SceneContent 
                isLoadingGallery={isLoadingGallery}
                galleryData={galleryData}
                assets={assets}
                roomLoaded={roomLoaded}
                texturesLoaded={texturesLoaded}
                avatarLoaded={avatarLoaded}
                onLoadingComplete={handleLoadingComplete}
                onSpotSelect={onSpotSelect}
                isViewMode={propIsViewMode}
                galleryId={galleryId}
                isRightMenuOpen={isRightMenuOpen}
                initialFov={initialFov}
                onAssetsUpdate={propOnAssetsUpdate}
                currentGallery={currentGallery}
                glbPath={propGlbPath}
                ref={ref}
                rendererRef={rendererRef}
                sceneRef={sceneRef}
                cameraRef={cameraRef}
                isMobile={isMobile}
                handleBackgroundClick={handleBackgroundClick}
                setAvatarLoaded={setAvatarLoaded}
                currentAssets={currentAssets}
                handleAssetsUpdate={handleAssetsUpdate}
                roomType={roomType}
                glbPath={glbPath}
                setRoomLoaded={setRoomLoaded}
                setTexturesLoaded={setTexturesLoaded}
                handleAssetHover={handleAssetHover}
                hoveredAssetId={hoveredAssetId}
                sceneManager={sceneManager}
                spawnPoint={spawnPoint}
            />
            {/* Show tooltip only if user hasn't entered view mode yet and not already in view mode */}
            {showFloatingTooltip && !effectiveViewMode && galleryId && (
                <FloatingTooltip 
                    isVisible={true} 
                    onFirstClick={() => setShowFloatingTooltip(false)}
                />
            )}
        </PointerLockProvider>
    );
});

function CrosshairComponent({ isPointerLocked, controlsRef, isViewMode }) {
    const [isFirstPerson, setIsFirstPerson] = useState(false);
    
    useEffect(() => {
        const checkCameraDistance = () => {
            if (controlsRef.current?.camera) {
                const camera = controlsRef.current.camera;
                const distance = camera.position.length();
                // If camera is very close to origin, it's probably first person
                setIsFirstPerson(distance < 2);
            }
        };
        
        const interval = setInterval(checkCameraDistance, 100);
        return () => clearInterval(interval);
    }, [controlsRef]);
    
    // Show crosshair in view mode when in first person, or when pointer is locked in any mode
    if (!isFirstPerson || (!isViewMode && !isPointerLocked)) return null;
    
    return (
        <div 
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '4px',
                height: '4px',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(0, 0, 0, 0.3)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 1000
            }}
        />
    );
}

function SceneContent({ 
    isLoadingGallery, 
    galleryData, 
    assets, 
    roomLoaded, 
    texturesLoaded, 
    avatarLoaded, 
    onLoadingComplete,
    onSpotSelect,
    isViewMode,
    galleryId,
    isRightMenuOpen,
    initialFov,
    onAssetsUpdate,
    currentGallery,
    glbPath: propGlbPath,
    rendererRef,
    sceneRef,
    cameraRef,
    isMobile,
    handleBackgroundClick,
    setAvatarLoaded,
    currentAssets,
    handleAssetsUpdate,
    roomType,
    glbPath,
    setRoomLoaded,
    setTexturesLoaded,
    handleAssetHover,
    hoveredAssetId,
    sceneManager,
    spawnPoint
}) {
    const [isPointerLocked, setIsPointerLocked] = useState(false);
    
    useEffect(() => {
        const checkPointerLock = () => {
            const locked = !!document.pointerLockElement;
            setIsPointerLocked(locked);
        };
        
        document.addEventListener('pointerlockchange', checkPointerLock);
        return () => document.removeEventListener('pointerlockchange', checkPointerLock);
    }, []);
    
    const {
        controlsRef,
        sceneContainerRef,
        isLocked: sceneManagerIsLocked,
        handleLock,
        handleUnlock,
        managedAssets,
        selectedAsset,
        handleSpotSelection,
        canInteract,
        fov,
        setFov,
        effectiveViewMode,
        handleViewModeToggle,
        setSelectedSpot,
        setCanInteract,
        isShowMode
    } = sceneManager;

    return (
        <>
            {/* Loading Overlay - moved outside container */}
            <GalleryLoadingOverlay 
                isVisible={isLoadingGallery}
                galleryData={galleryData}
                assets={assets}
                roomLoaded={roomLoaded}
                texturesLoaded={texturesLoaded}
                avatarLoaded={avatarLoaded}
                onLoadingComplete={onLoadingComplete}
            />
            
            {/* Crosshair - only show in first person mode */}
            <CrosshairComponent 
                isPointerLocked={isPointerLocked}
                controlsRef={controlsRef}
                isViewMode={effectiveViewMode}
            />
            
            <div
                ref={sceneContainerRef}
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    border: !effectiveViewMode && galleryId ? '4px solid #00FFA3' : 'none',
                    boxSizing: 'border-box'
                }}
                className="scene-container"
            >
                {galleryId && (
                    <>
                        <ControlInstructions isLocked={isPointerLocked} isViewMode={effectiveViewMode} />
                        <ModeIndicator isShowMode={effectiveViewMode} />
                    </>
                )}

                <Canvas
                    onCreated={({ gl, scene, camera }) => {
                        console.log('🎯 THIS IS THE ACTUAL CAMERA BEING USED FOR WALKING:', camera);
                        console.log('🎯 Camera position:', camera.position);
                        console.log('🎯 Camera FOV:', camera.fov);
                        rendererRef.current = gl;
                        
                        // Create a neutral environment for reflections
                        const pmremGenerator = new THREE.PMREMGenerator(gl);
                        const neutralColor = new THREE.Color(0.8, 0.8, 0.9); // Soft blue-white
                        const neutralScene = new THREE.Scene();
                        neutralScene.background = neutralColor;
                        const neutralEnvTexture = pmremGenerator.fromScene(neutralScene).texture;
                        scene.environment = neutralEnvTexture; // Set neutral environment for reflections
                        sceneRef.current = scene;
                        cameraRef.current = camera;
                        
                        // Configure renderer with mobile optimizations
                        const pixelRatio = isMobile ? Math.min(window.devicePixelRatio, 1.5) : window.devicePixelRatio;
                        gl.setPixelRatio(pixelRatio);
                        gl.setSize(window.innerWidth, window.innerHeight);
                        
                        // Mobile-specific optimizations
                        if (isMobile) {
                            gl.shadowMap.enabled = false; // Disable shadows
                            gl.outputColorSpace = THREE.SRGBColorSpace;
                            gl.toneMapping = THREE.NoToneMapping;
                            
                            // Additional mobile optimizations
                            gl.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Cap pixel ratio
                            gl.precision = 'mediump'; // Use medium precision
                        } else {
                            // Desktop optimizations
                            gl.shadowMap.enabled = true;
                            gl.shadowMap.type = THREE.PCFSoftShadowMap; // Better quality shadows
                            gl.outputColorSpace = THREE.SRGBColorSpace;
                            gl.toneMapping = THREE.ACESFilmicToneMapping;
                            gl.toneMappingExposure = 1.0;
                        }
                    }}
                    onClick={handleBackgroundClick}
                    gl={{
                        antialias: !isMobile, // Disable antialiasing on mobile
                        alpha: true,
                        powerPreference: isMobile ? "low-power" : "high-performance",
                        stencil: false,
                        depth: true,
                        // Additional performance settings
                        logarithmicDepthBuffer: false,
                        preserveDrawingBuffer: false,
                        failIfMajorPerformanceCaveat: false
                    }}
                    // Performance settings
                    frameloop="always" // Keep continuous rendering for movement
                    dpr={isMobile ? [1, 1.5] : [1, 2]} // Adaptive pixel ratio
                >
                    {galleryId ? (
                        <>
                            {console.log('🎮 Scene: Gallery mode active with galleryId:', galleryId)}
                            <SceneCamera
                                fov={fov}
                                height={CAMERA_CONFIG.height}
                                allowPositionOverride={true}
                                spawnPoint={spawnPoint}
                            />

                            <SceneControls
                                ref={controlsRef}
                                isLocked={isPointerLocked}
                                onLock={handleLock}
                                onUnlock={handleUnlock}
                                onViewModeToggle={handleViewModeToggle}
                                spawnPoint={spawnPoint}
                            />


                            {/* Background color - changing to a very subtle warm gray */}
                            <color attach="background" args={[0xf2f0ed]} />
                            <fog attach="fog" args={[0xf2f0ed, 60, 100]} />
                            
                            {/* Use HDR only for lighting, not reflections */}
                            <Environment 
                                files={resolvePath('/textures/hdr/studio_small_08_4k.hdr')} 
                                background={false}
                                environmentIntensity={0}
                                intensity={2.0}
                            />
                            
                            {/* Very bright, studio-quality lighting */}
                            <ambientLight intensity={2.0} color="#ffffff" />
                            
                            {/* Strong directional lights for definition */}
                            <directionalLight 
                                position={[5, 10, 5]} 
                                intensity={1.0} 
                                color="#ffffff" 
                                castShadow={false}
                            />
                            
                            <directionalLight 
                                position={[-5, 10, -5]} 
                                intensity={1.0} 
                                color="#ffffff" 
                                castShadow={false}
                            />
                            
                            {/* Strong fill lights everywhere */}
                            <pointLight position={[0, 8, 0]} intensity={0.8} color="#ffffff" />
                            <pointLight position={[15, 6, 15]} intensity={0.6} color="#ffffff" />
                            <pointLight position={[-15, 6, -15]} intensity={0.6} color="#ffffff" />
                            <pointLight position={[15, 6, -15]} intensity={0.6} color="#ffffff" />
                            <pointLight position={[-15, 6, 15]} intensity={0.6} color="#ffffff" />
                            
                            <Lighting roomType={roomType} />

                            {/* Avatar Test Component */}
                            {controlsRef.current && (
                                <AvatarTest 
                                    movementSystem={controlsRef.current.movementSystem}
                                    onAvatarLoaded={() => {
                                        console.log('Avatar loaded callback called');
                                        setAvatarLoaded(true);
                                    }}
                                />
                            )}
                            
                            <Room
                                onSpotSelect={handleSpotSelection}
                                assets={currentAssets}
                                canInteract={canInteract}
                                isShowMode={effectiveViewMode}
                                selectedSpot={selectedAsset}
                                galleryId={galleryId}
                                onAssetsUpdate={handleAssetsUpdate}
                                roomType={roomType}
                                glbPath={glbPath}
                                onRoomLoaded={() => setRoomLoaded(true)}
                                onTexturesLoaded={() => setTexturesLoaded(true)}
                                onHover={handleAssetHover}
                            />

                        </>
                    ) : null}

                    {/* Invisible plane for background clicks */}
                    {galleryId && (
                        <mesh position={[0, 0, -10]} onClick={handleBackgroundClick}>
                            <planeGeometry args={[1000, 1000]} />
                            <meshBasicMaterial visible={false} />
                        </mesh>
                    )}

                    {/* Post-processing effects - disabled on mobile for performance */}
                    {!isMobile && (
                        <EffectComposer multisampling={0}>
                            <N8AO
                                samples={32}
                                radius={0.2}
                                intensity={2.5}
                                aoRadius={0.3}
                                aoClamp={0.45}
                                color="black"
                            />
                        </EffectComposer>
                    )}
                </Canvas>

                {!isPointerLocked && galleryId && (
                    <>
                        <MobileControls
                            camera={controlsRef.current?.camera}
                            movementSystem={controlsRef.current?.movementSystem}
                        />
                        <MobileJoystick
                            movementSystem={controlsRef.current?.movementSystem}
                        />
                    </>
                )}

                {/* Press E to interact popup in view mode */}
                {effectiveViewMode && hoveredAssetId && currentAssets[hoveredAssetId]?.url && (
                    <div 
                        className="press-e-popup"
                        style={{
                            position: 'absolute',
                            bottom: 'calc(5rem + 50px)',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0, 0, 0, 0.8)',
                            color: '#00FFA3',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            border: '1px solid #00FFA3',
                            whiteSpace: 'nowrap',
                            backdropFilter: 'blur(10px)',
                            animation: 'fadeInOut 2s infinite',
                            pointerEvents: 'none',
                            userSelect: 'none',
                            zIndex: 4
                        }}
                    >
                        Press E to interact
                    </div>
                )}
            </div>
        </>
    );
}

export default React.memo(Scene);